import { ContractOutOfDefaultFieldResponse } from '@interfaces/contracts/responses/contract-field-change.response';
import { ClientContractRevisionAction } from '@interfaces/companys/contracts/responses/client-contract-revision.response';
import {
  ClientContractAutomaticData,
  ClientContractFieldKind,
  ClientContractFieldResponse,
  ClientContractStatus,
  ClientContractType,
} from '@interfaces/companys/contracts/responses/client-contract.response';
import { formatContractPercent } from '../../../partner/utils/partner-contract.utils';

/**
 * Peças de exibição dos contratos de cliente (card 3643). Só formatam o que o backend devolve: o
 * texto por extenso, a taxa mensal e qualquer outro número do contrato saem do backend.
 */

/** Os dois tipos, na ordem das abas e do seletor. */
export const CLIENT_CONTRACT_TYPES: readonly ClientContractType[] = ['FlatFee', 'ActiveManagement'];

const TYPE_LABELS: Record<ClientContractType, string> = {
  FlatFee: 'Flat Fee',
  ActiveManagement: 'Gestão Ativa',
};

/** "Flat Fee" / "Gestão Ativa". */
export function clientContractTypeLabel(
  type: ClientContractType | string | null | undefined
): string {
  return TYPE_LABELS[type as ClientContractType] ?? String(type ?? '');
}

/** Parâmetro de URL da aba do modelo (`?type=ga`). */
export function clientContractTypeFromQuery(value: string | null): ClientContractType {
  return value === 'ga' ? 'ActiveManagement' : 'FlatFee';
}

export function clientContractTypeToQuery(type: ClientContractType): string | null {
  return type === 'ActiveManagement' ? 'ga' : null;
}

/** A minuta está finalizada (o número fica aceito por segurança, como na parceria). */
export function isClientContractFinalized(
  status: ClientContractStatus | number | null | undefined
): boolean {
  return status === 'Finalized' || status === 1;
}

export function clientContractStatusLabel(
  status: ClientContractStatus | number | null | undefined
): string {
  return isClientContractFinalized(status) ? 'Finalizado' : 'Rascunho';
}

const REVISION_ACTION_LABELS: Record<ClientContractRevisionAction, string> = {
  Created: 'Criado',
  Edited: 'Editado',
  Finalized: 'Finalizado',
  Reopened: 'Reaberto',
  TextEdited: 'Texto alterado',
};

export function clientContractRevisionActionLabel(action: string | null | undefined): string {
  return REVISION_ACTION_LABELS[action as ClientContractRevisionAction] ?? String(action ?? '');
}

/** Valor em reais vindo como número ou texto invariante ("27000000.00"): "R$ 27.000.000,00". */
export function formatContractMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Formata conforme o tipo do campo (R$ ou %). */
export function formatClientContractValue(
  value: number | string | null | undefined,
  kind: ClientContractFieldKind | null | undefined
): string {
  return kind === 'Money' ? formatContractMoney(value) : formatContractPercent(value);
}

/**
 * Formatador do histórico ("Alterações de valores"): acha o tipo do campo pela chave no catálogo.
 * Campo que não está no catálogo cai no percentual (todos os campos de cliente, menos a
 * necessidade de crédito, são percentuais).
 */
export function clientContractValueFormatter(
  fields: ClientContractFieldResponse[]
): (value: number | string | null | undefined, fieldKey?: string) => string {
  return (value, fieldKey) =>
    formatClientContractValue(value, fields.find(field => field.key === fieldKey)?.kind);
}

/**
 * "Taxa de gestão 1,5% (padrão 1%); Valor da necessidade de crédito ...", para o banner de
 * aprovação. O tipo de cada campo vem do catálogo.
 */
export function describeClientOutOfDefaultFields(
  outOfDefault: ContractOutOfDefaultFieldResponse[] | null | undefined,
  fields: ClientContractFieldResponse[]
): string {
  return (outOfDefault ?? [])
    .map(item => {
      const kind = fields.find(field => field.key === item.key)?.kind;
      return `${item.label} ${formatClientContractValue(item.currentValue, kind)} (padrão ${formatClientContractValue(item.defaultValue, kind)})`;
    })
    .join('; ');
}

/** Linha de qualificação para leitura ("Rua X, 100, sala 2, Centro, 01000-000, São Paulo/SP"). */
export function describeClientContractAddress(data: ClientContractAutomaticData | null): string {
  if (!data) return '';
  const cityState = [data.city, data.state].filter(Boolean).join('/');
  return [data.street, data.number, data.complement, data.neighborhood, data.zipCode, cityState]
    .filter(part => !!part && String(part).trim())
    .join(', ');
}
